export interface PipelineStage {
  id: string;       // stage code from API, e.g. 'LO', 'HIT'
  numericId: number; // database PK for API calls (gate criteria, transitions)
  label: string;    // full name
  short: string;    // code displayed in headers
  color: string;
  description: string;
}

export interface PipelineProject {
  id: number;
  name: string;
  target: string;
  indication: string;          // therapeutic_area_name
  indicationCode: string;      // therapeutic_area code
  stage: string;               // stage code, e.g. 'LO'
  status: 'active' | 'on_hold' | 'at_risk' | 'cancelled' | 'completed';
  lead: string;
  team: string[];
  compounds: number;
  hits: number;
  leads: number;
  startDate: string;
  lastUpdate: string;
  progress: number;
  milestones: { name: string; done: boolean }[];
  notes: string;
  cancelReason?: string;
  cancelDate?: string;
}

export interface TherapeuticArea {
  code: string;
  name: string;
  description?: string;
}

// Stage colors by code — consistent visual identity
const STAGE_COLORS: Record<string, string> = {
  IDE: '#8b5cf6',   // Legacy code from API
  DISC: '#8b5cf6',  // Display code (Discovery)
  HIT: '#6366f1',
  H2L: '#3b82f6',
  LO:  '#0ea5e9',
  CS:  '#14b8a6',
  PC:  '#22c55e',
  IND: '#84cc16',
  PH1: '#eab308',
  PH2: '#f97316',
  PH3: '#ef4444',
};

export const getStageColor = (code: string): string =>
  STAGE_COLORS[code] || '#94a3b8';

// These are populated from the API at runtime
let _stages: PipelineStage[] = [];

export const setStages = (stages: PipelineStage[]): void => {
  _stages = stages;
};

export const getStages = (): PipelineStage[] => _stages;

export const getStageIndex = (stageCode: string): number =>
  _stages.findIndex((s) => s.id === stageCode);

export const getStage = (stageCode: string): PipelineStage | undefined =>
  _stages.find((s) => s.id === stageCode);

export const getNextStage = (stageCode: string): PipelineStage | undefined => {
  const idx = _stages.findIndex((s) => s.id === stageCode);
  return idx >= 0 && idx < _stages.length - 1 ? _stages[idx + 1] : undefined;
};

/** Convert API pipeline-stage response to a PipelineStage */
export const mapApiStage = (api: {
  id: number;
  code: string;
  name: string;
  description?: string;
  typical_months?: string;
  sort_order: number;
}): PipelineStage => {
  // Remap stage codes for display headers
  const CODE_DISPLAY: Record<string, string> = { IDE: 'DISC', CS: 'DEV', IND: 'IND/CTX' };
  const NAME_DISPLAY: Record<string, string> = { IDE: 'Discovery' };
  const displayCode = CODE_DISPLAY[api.code] || api.code;
  const displayName = NAME_DISPLAY[api.code] || api.name;
  return {
    id: api.code,
    numericId: api.id,
    label: displayName,
    short: displayCode,
    color: getStageColor(api.code),
    description: api.description || '',
  };
};

/** Convert API portfolio/summary row to a PipelineProject */
export const mapApiProject = (api: {
  id: number;
  name: string;
  target_name?: string;
  status?: string;
  therapeutic_area?: string;
  therapeutic_area_name?: string;
  stage_code?: string;
  compound_count?: number;
  leader_name?: string;
  leader_id?: string;
  progress?: number;
  created_at?: string;
  updated_at?: string;
}): PipelineProject => ({
  id: api.id,
  name: api.name,
  target: api.target_name || '',
  indication: api.therapeutic_area_name || '',
  indicationCode: api.therapeutic_area || '',
  stage: api.stage_code || '',
  status: (api.status as PipelineProject['status']) || 'active',
  lead: api.leader_name || 'Unassigned',
  team: [],
  compounds: api.compound_count || 0,
  hits: 0,
  leads: 0,
  startDate: api.created_at || '',
  lastUpdate: api.updated_at || api.created_at || '',
  progress: api.progress || 0,
  milestones: [],
  notes: '',
});
